import React from 'react';
import { Card, Table, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * PensionProjection Component
 *
 * Shows 3 growth scenarios (FCA standard rates):
 * - Low: 2% per year
 * - Medium: 5% per year
 * - High: 8% per year
 *
 * For each scenario shows:
 * - Pot at retirement
 * - Annuity income (6% of pot)
 * - Drawdown "age of ruin" (50% salary withdrawal)
 */

const GROWTH_RATES = { low: 0.02, medium: 0.05, high: 0.08 };
const ANNUITY_RATE = 0.06;
const DRAWDOWN_WITHDRAWAL_RATE = 0.50;

const PensionProjection = ({
                               currentPotValue,
                               annualContribution,
                               yearsToRetirement,
                               currentSalary,
                               retirementAge,
                               recommendedContribution
                           }) => {

    const calculateFuturePot = (currentPot, annualContrib, years, growthRate) => {
        let pot = currentPot;
        for (let i = 0; i < years; i++) {
            pot = (pot + annualContrib) * (1 + growthRate);
        }
        return pot;
    };

    const calculateDrawdownAge = (potAtRetirement, annualWithdrawal, startAge, growthRate) => {
        let pot = potAtRetirement;
        let age = startAge;
        while (pot > 0 && age < 120) {
            pot = pot * (1 + growthRate) - annualWithdrawal;
            age++;
        }
        return age >= 120 ? '100+' : age;
    };

    const annualWithdrawal = currentSalary * DRAWDOWN_WITHDRAWAL_RATE;
    const hasRecommendation = recommendedContribution && recommendedContribution !== annualContribution;

    const scenarios = ['low', 'medium', 'high'].map(scenario => {
        const currentPot = calculateFuturePot(currentPotValue, annualContribution, yearsToRetirement, GROWTH_RATES[scenario]);
        const recommendedPot = hasRecommendation
            ? calculateFuturePot(currentPotValue, recommendedContribution, yearsToRetirement, GROWTH_RATES[scenario])
            : null;
        const drawdownGrowth = scenario === 'low' ? 0.01 : scenario === 'medium' ? 0.03 : 0.04;

        return {
            name: scenario,
            label: scenario === 'low' ? 'Cautious (2%)' : scenario === 'medium' ? 'Moderate (5%)' : 'Adventurous (8%)',
            currentPot,
            currentAnnuity: currentPot * ANNUITY_RATE,
            currentDrawdownAge: calculateDrawdownAge(currentPot, annualWithdrawal, retirementAge, drawdownGrowth),
            recommendedPot,
            recommendedAnnuity: recommendedPot ? recommendedPot * ANNUITY_RATE : null,
            recommendedDrawdownAge: recommendedPot ? calculateDrawdownAge(recommendedPot, annualWithdrawal, retirementAge, drawdownGrowth) : null
        };
    });

    const formatCurrency = (value) => new Intl.NumberFormat('en-GB', {
        style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(value);

    return (
        <div className="pension-projection">
            <h5 className="mb-3">
                <FontAwesomeIcon icon="calculator" className="me-2 text-primary" />
                Pension Projection
            </h5>

            <Card>
                <Card.Body>
                    <Table responsive className="mb-0">
                        <thead>
                        <tr>
                            <th>Scenario</th>
                            <th className="text-end">Pot at {retirementAge}</th>
                            <th className="text-end">Annuity Income</th>
                            <th className="text-end">Drawdown Until</th>
                            {hasRecommendation && (
                                <>
                                    <th className="text-end text-success">Recommended Pot</th>
                                    <th className="text-end text-success">Rec. Annuity</th>
                                </>
                            )}
                        </tr>
                        </thead>
                        <tbody>
                        {scenarios.map(s => (
                            <tr key={s.name}>
                                <td>
                    <span className={`badge ${s.name === 'low' ? 'bg-secondary' : s.name === 'medium' ? 'bg-primary' : 'bg-success'}`}>
                      {s.label}
                    </span>
                                </td>
                                <td className="text-end">{formatCurrency(s.currentPot)}</td>
                                <td className="text-end">{formatCurrency(s.currentAnnuity)}/yr</td>
                                <td className="text-end">
                                    Age {s.currentDrawdownAge}
                                    {s.currentDrawdownAge !== '100+' && s.currentDrawdownAge < 85 && (
                                        <FontAwesomeIcon icon="exclamation-triangle" className="ms-1 text-warning" title="May run out early" />
                                    )}
                                </td>
                                {hasRecommendation && (
                                    <>
                                        <td className="text-end text-success fw-bold">{formatCurrency(s.recommendedPot)}</td>
                                        <td className="text-end text-success">{formatCurrency(s.recommendedAnnuity)}/yr</td>
                                    </>
                                )}
                            </tr>
                        ))}
                        </tbody>
                    </Table>

                    {hasRecommendation && (
                        <Alert variant="success" className="mt-3 mb-0">
                            <FontAwesomeIcon icon="chart-line" className="me-2" />
                            <strong>+{formatCurrency(scenarios[1].recommendedPot - scenarios[1].currentPot)}</strong> potential improvement (moderate scenario)
                        </Alert>
                    )}
                </Card.Body>
            </Card>

            <div className="text-muted small mt-2">
                <FontAwesomeIcon icon="info-circle" className="me-1" />
                FCA standard rates. Annuity: {ANNUITY_RATE * 100}% of pot. Drawdown: {formatCurrency(annualWithdrawal)}/yr ({DRAWDOWN_WITHDRAWAL_RATE * 100}% salary).
            </div>
        </div>
    );
};

export default PensionProjection;